// 2026-09-23 - FormuLearning - 성분 중요도 (COMP-FEATURE-IMPORTANCE, 이슈 #67 S4-7)
// 파일 위치: frontend/src/features/step4/FeatureImportance.tsx
// 목적: 최적화 결과의 **가장 첫 부분**(UI/UX v2.7 §4.6 · M84-30·66). 목적함수마다 한 벌, 성분별 가로 막대 —
//       값은 GP length_scale 에서 온 % (합 100, 공정변수 포함). 원천은 `GET …/analysis` 의 `importance` 이며
//       전체 누적 관측이라 라운드 선택과 무관하다(BR-OPT-17). 새 분석이 돌면 값을 그대로 두고 배지만 단다(D-S4-2).

import type { Step4AnalysisView } from '../../api/step4'

/** 기준 시각 `분석 09/21 14:05` — 분석 등록 시각(`created_at`)을 이 브라우저 시간대로. */
function analysisStamp(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const p = (n: number) => String(n).padStart(2, '0')
  return `분석 ${p(d.getMonth() + 1)}/${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`
}

export function RecalcBadge() {
  return (
    <span
      className="rounded border px-1.5 py-0.5 text-[10px] font-semibold"
      data-testid="recalc-badge"
      style={{ borderColor: 'var(--border-purple)', color: 'var(--color-accent-purple)' }}
    >
      ⟳ 재계산 중
    </span>
  )
}

export default function FeatureImportance({ view }: { view: Step4AnalysisView }) {
  const a = view.analysis
  if (!a) return null
  const objectives = Object.entries(a.importance ?? {})
  if (objectives.length === 0) return null

  return (
    <section className="mb-3" data-testid="feature-importance">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-text-secondary">Feature Importance</h4>
        {view.recalculating && <RecalcBadge />}
        <span className="text-[10px] text-text-muted" data-testid="analysis-stamp">{analysisStamp(a.created_at)}</span>
      </div>
      {view.last_failed && (
        <p className="mb-1 text-[10px]" style={{ color: 'var(--color-warn)' }} data-testid="analysis-failed">
          ⚠ 최근 재계산 실패 — 이전 결과 표시 중
        </p>
      )}
      <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))' }}>
        {objectives.map(([obj, vars]) => {
          const rows = Object.entries(vars).sort((x, y) => y[1] - x[1])
          const max = Math.max(...rows.map(([, v]) => v), 1)
          return (
            <div key={obj} className="rounded border p-2" style={{ borderColor: 'var(--border-subtle)' }}
                 data-testid={`importance-${obj}`}>
              <p className="mb-1.5 text-[11px] font-semibold text-text-body">{obj}</p>
              <ul className="space-y-1">
                {rows.map(([name, v]) => (
                  <li key={name} className="grid items-center gap-1.5 text-[10px]"
                      style={{ gridTemplateColumns: 'minmax(0, 9rem) 1fr 2.2rem' }}>
                    <span className="truncate text-text-secondary" title={name}>{name}</span>
                    <span className="h-2 rounded-sm" style={{ background: 'var(--bg-chip-purple)' }}>
                      <span className="block h-2 rounded-sm"
                            style={{ width: `${(v / max) * 100}%`, background: 'var(--color-accent-purple)' }} />
                    </span>
                    <span className="text-right tabular-nums text-text-body">{Math.round(v)}%</span>
                  </li>
                ))}
              </ul>
            </div>
          )
        })}
      </div>
    </section>
  )
}
